import * as React from "react";
import { Input } from "components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { useLeadsImport } from "./useLeadsImport";
import { Loader } from "components/ui/loader";
import {
    File as FileIcon, FileUp, FolderPlus,
    Check,
    Lock,
    Cog,
    X,
    Upload,
    AlertCircle,
    CheckCircle2,
    Download,
} from "lucide-react";

export default function LeadsImport({ updateCustomerGroup }: any) {
    const {
        isOpen, setIsOpen,
        currentStep, setCurrentStep, goNext,
        selectedFiles,
        fileInputRef,
        folderInputRef,
        onAddFiles,
        onAddFolder,
        onFileChange,
        onFolderChange,
        removeFile,
        dataKeys,
        sheetHeaders,
        errorMsg,
        onChangeMapping,
        validated,
        hasErrors,
        skipRow,
        skipAll,
        isWorking,
        toastMsg,
        canNext,
        onFinishImport,
        resetAll,
    } = useLeadsImport({ updateCustomerGroup });

    const totalRows = validated.length;
    const invalidRows = validated.filter((r: any) => r.emailError).length;
    const validRows = Math.max(totalRows - invalidRows, 0);
    const pct = totalRows ? Math.round((validRows / totalRows) * 100) : 0;

    const steps = [
        { label: "Add File", icon: FileIcon },
        { label: "Map Columns", icon: Cog },
        { label: "Validate & Import", icon: Lock },
    ];

    const Stepper = () => (
        <div className="flex items-center w-full">
            {steps.map((step, i) => {
                const Icon = step.icon;
                const done = currentStep > i;
                const active = currentStep === i;
                return (
                    <React.Fragment key={i}>
                        <div className="flex flex-col items-center gap-1.5 min-w-[90px]">
                            <div className={`flex h-9 w-9 items-center justify-center rounded-full text-white shadow-sm transition-all ${done ? "bg-emerald-500" : active ? "bg-blue-600" : "bg-gray-200 dark:bg-dark-600"
                                }`}>
                                {done ? <Check size={16} /> : <Icon size={16} className={active ? "text-white" : "text-content-subtle dark:text-gray-500"} />}
                            </div>
                            <span className={`text-micro font-medium whitespace-nowrap ${active ? "text-blue-600 dark:text-blue-400" : done ? "text-emerald-600 dark:text-emerald-400" : "text-content-subtle dark:text-gray-500"
                                }`}>
                                {step.label}
                            </span>
                        </div>
                        {i < steps.length - 1 && (
                            <div className={`h-[2px] flex-1 rounded transition-all mx-2 mb-4 ${done ? "bg-emerald-400" : "bg-gray-200 dark:bg-dark-600"}`} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(v) => {
                if (v) setIsOpen(true);
                else { setIsOpen(false); resetAll(); }
            }}
        >
            {/* Trigger buttons — flat siblings so PageHeader layout applies per-button */}
            <a
                href="https://drive.google.com/uc?export=download&id=1SSrCiFo8V-svpefOD03l6NPjSLjxOU50"
                target="_blank"
                rel="noreferrer"
                download="leads_sample.csv"
                className="inline-flex items-center justify-center gap-1.5
                    px-3 sm:px-4 py-2 sm:py-2.5 min-h-[36px] sm:min-h-[38px]
                    border border-line dark:border-dark-600
                    bg-surface dark:bg-dark-800
                    hover:bg-gray-50 dark:hover:bg-dark-700
                    text-gray-700 dark:text-gray-200
                    text-meta sm:text-ui font-semibold
                    whitespace-nowrap
                    rounded-xl shadow-sm hover:shadow-md
                    transition-all duration-200
                    w-full sm:w-auto"
            >
                <Download size={14} />
                Sample
            </a>
            <DialogTrigger asChild>
                <button
                    className="inline-flex items-center justify-center gap-1.5
                        px-3 sm:px-4 py-2 sm:py-2.5 min-h-[36px] sm:min-h-[38px]
                        bg-gradient-to-r from-blue-600 to-indigo-600
                        hover:from-blue-700 hover:to-indigo-700
                        active:from-blue-800 active:to-indigo-800
                        text-white text-meta sm:text-ui font-semibold
                        whitespace-nowrap
                        rounded-xl shadow-sm hover:shadow-md
                        transition-all duration-200
                        w-full sm:w-auto"
                >
                    <Upload size={14} />
                    Import
                </button>
            </DialogTrigger>

            <DialogContent
                className="w-[min(92vw,900px)] max-w-none bg-surface dark:bg-dark-800 border border-line dark:border-dark-700 rounded-2xl shadow-2xl p-0 overflow-hidden"
                onInteractOutside={(e) => e.preventDefault()}
            >

                <div className="p-6 space-y-6">
                    <DialogHeader>
                        <DialogTitle className="text-[17px] font-bold text-content dark:text-white">
                            Import Leads
                        </DialogTitle>
                        <p className="text-[13px] text-content-muted dark:text-gray-400 mt-0.5">
                            Upload your contact list via Excel or CSV
                        </p>
                    </DialogHeader>

                    {/* Error banners */}
                    {errorMsg?.map((it: any, i: number) =>
                        it.shown ? (
                            <div key={i} className="flex items-start gap-2.5 rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-400">
                                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                                {it.name}
                            </div>
                        ) : null
                    )}

                    {/* Stepper */}
                    <Stepper />

                    {/* ── STEP 0: Upload ── */}
                    {currentStep === 0 && (
                        <div className="rounded-xl border border-line dark:border-dark-700 bg-gray-50 dark:bg-dark-700/40 p-6">
                            <div className="flex flex-col items-center justify-center gap-3 text-center">
                                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800/40">
                                    <FileUp size={26} className="text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                    <h3 className="text-[15px] font-semibold text-content dark:text-white">Upload Your Contact List</h3>
                                    <p className="mt-1 text-[12px] text-content-muted dark:text-gray-400">
                                        Supported formats: .xlsx, .xls, .csv, .ods
                                    </p>
                                </div>

                                {/* Hidden inputs */}
                                <Input ref={fileInputRef as any} type="file" accept=".xlsx,.xls,.csv,.ods" multiple className="hidden" onChange={onFileChange} />
                                {/* @ts-ignore */}
                                <input ref={folderInputRef as any} type="file" webkitdirectory="" className="hidden" onChange={onFolderChange} />

                                <div className="flex items-center gap-3 mt-2">
                                    <button
                                        onClick={onAddFiles}
                                        className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:opacity-90 text-white px-5 py-2.5 rounded-xl text-sm font-medium shadow-sm transition-opacity"
                                    >
                                        <FileUp size={16} />
                                        Add File
                                    </button>
                                    <button
                                        onClick={onAddFolder}
                                        className="flex items-center gap-2 border border-line dark:border-dark-600 bg-surface dark:bg-dark-700 hover:bg-gray-50 dark:hover:bg-dark-600 text-gray-700 dark:text-gray-200 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors"
                                    >
                                        <FolderPlus size={16} />
                                        Add Folder
                                    </button>
                                </div>

                                {toastMsg && (
                                    <div className="w-full rounded-lg border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/20 p-2.5 text-xs text-amber-700 dark:text-amber-400 text-center">
                                        {toastMsg}
                                    </div>
                                )}
                            </div>

                            {/* Selected files */}
                            {selectedFiles.length > 0 && (
                                <div className="mt-5 pt-5 border-t border-line dark:border-dark-600">
                                    <p className="text-[11px] font-medium text-content-muted dark:text-gray-400 uppercase tracking-wide mb-2.5">
                                        Selected files ({selectedFiles.length})
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedFiles.map((f: File, idx: number) => (
                                            <div key={idx} className="flex items-center gap-2 rounded-full border border-line dark:border-dark-600 bg-surface dark:bg-dark-700 px-3 py-1.5 text-sm shadow-sm">
                                                <FileIcon size={14} className="text-blue-500 dark:text-blue-400 shrink-0" />
                                                <span className="max-w-[220px] truncate text-gray-700 dark:text-gray-200 text-ui">{f.name}</span>
                                                <button
                                                    title="Remove"
                                                    className="ml-0.5 rounded-full p-0.5 hover:bg-red-50 dark:hover:bg-red-900/30 text-content-subtle hover:text-red-500 dark:hover:text-red-400 transition-colors"
                                                    onClick={() => removeFile(idx)}
                                                    type="button"
                                                >
                                                    <X size={13} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── STEP 1: Data Mapping ── */}
                    {currentStep === 1 && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3 rounded-xl bg-gray-50 dark:bg-dark-700/40 border border-line dark:border-dark-700 px-4 py-2.5 text-[12px] font-semibold text-content-muted dark:text-gray-400 uppercase tracking-wide">
                                <div>Available Columns</div>
                                <div>Map to Sheet Column</div>
                            </div>

                            <div className="space-y-2 max-h-72 overflow-auto no-scrollbar pr-1">
                                {dataKeys.map((k: any, idx: number) => (
                                    <div key={idx} className="grid grid-cols-2 gap-3 items-center rounded-xl border border-line dark:border-dark-700 bg-surface dark:bg-dark-800 px-4 py-2.5">
                                        <span className={`text-ui font-medium truncate ${k.error ? "text-red-500" : "text-content dark:text-gray-100"}`}>
                                            {k.error ? `⚠ ${k.available}` : k.available}
                                        </span>
                                        <Select value={k.selected} onValueChange={(val) => onChangeMapping(idx, val)}>
                                            <SelectTrigger className="h-9 text-[13px] border-line dark:border-dark-600 bg-gray-50 dark:bg-dark-700 rounded-lg">
                                                <SelectValue placeholder="Select column…" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {sheetHeaders.map((h: any) => (
                                                    <SelectItem
                                                        key={h.value}
                                                        value={h.value}
                                                        className={`text-[13px] ${h.taken && h.value !== k.selected ? 'opacity-50' : ''}`}
                                                    >
                                                        {h.value}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── STEP 2: Validate ── */}
                    {currentStep === 2 && (
                        <div className="space-y-4">
                            {/* Progress header */}
                            <div className="rounded-xl border border-line dark:border-dark-700 bg-gray-50 dark:bg-dark-700/40 p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="text-[13px] text-content-secondary dark:text-gray-300">
                                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">{validRows} valid</span>
                                        {" "}of {totalRows} rows
                                        {invalidRows > 0 && (
                                            <span className="ml-2 text-red-500 dark:text-red-400">({invalidRows} invalid)</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        {pct === 100 ? (
                                            <CheckCircle2 size={16} className="text-emerald-500 dark:text-emerald-400" />
                                        ) : (
                                            <AlertCircle size={16} className="text-amber-500 dark:text-amber-400" />
                                        )}
                                        <span className="text-[13px] font-semibold text-content dark:text-white">{pct}%</span>
                                    </div>
                                </div>
                                {/* Progress bar */}
                                <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-dark-600 overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all duration-300 ${pct === 100 ? "bg-emerald-500" : "bg-blue-500"}`}
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>
                            </div>

                            {hasErrors ? (
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 rounded-xl border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/20 p-3 text-[13px] text-amber-700 dark:text-amber-400">
                                        <AlertCircle size={15} className="shrink-0" />
                                        Some rows have invalid emails. Fix or skip them to continue.
                                    </div>
                                    <div className="max-h-64 overflow-auto rounded-xl border border-line dark:border-dark-700">
                                        {validated.map((r: any, idx: number) =>
                                            r.emailError ? (
                                                <div key={idx} className="flex items-center justify-between gap-3 border-b border-line-subtle dark:border-dark-700 px-4 py-2.5 text-[13px] last:border-b-0 bg-surface dark:bg-dark-800">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-content dark:text-gray-100">Row {r.rowIndex ?? idx}</div>
                                                        <div className="text-content-muted dark:text-gray-400 truncate">
                                                            Email: <span className="font-mono text-red-500 dark:text-red-400">{r.Email || "(empty)"}</span>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => skipRow(idx)}
                                                        className="shrink-0 text-[12px] font-medium px-3 py-1.5 rounded-lg border border-line dark:border-dark-600 bg-surface dark:bg-dark-700 text-content-secondary dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 transition-colors"
                                                    >
                                                        Skip
                                                    </button>
                                                </div>
                                            ) : null
                                        )}
                                    </div>
                                    <div className="flex justify-center">
                                        <button
                                            onClick={skipAll}
                                            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:opacity-90 text-white px-6 py-2.5 rounded-xl text-sm font-medium shadow-sm transition-opacity"
                                        >
                                            Skip All Invalid Rows
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2 py-6 text-center">
                                    <CheckCircle2 size={36} className="text-emerald-500 dark:text-emerald-400" />
                                    <p className="text-[14px] font-semibold text-content dark:text-white">All rows are valid!</p>
                                    <p className="text-[13px] text-content-muted dark:text-gray-400">Click Finish to import {validRows} leads.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-line-subtle dark:border-dark-700 bg-gray-50/50 dark:bg-dark-800/50">
                    <div>
                        {isWorking && (
                            <div className="flex items-center gap-2 text-[13px] text-content-secondary dark:text-gray-300">
                                <Loader size={16} />
                                <span>Importing leads…</span>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2.5">
                        {currentStep > 0 && (
                            <button
                                className="text-[13px] font-medium px-4 py-2 rounded-xl border border-line dark:border-dark-600 bg-surface dark:bg-dark-700 text-content-secondary dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 transition-colors disabled:opacity-50"
                                onClick={() => setCurrentStep((prev: any) => Math.max(0, prev - 1) as any)}
                                disabled={isWorking}
                            >
                                Back
                            </button>
                        )}
                        {currentStep < 2 ? (
                            <button
                                className="text-[13px] font-semibold px-5 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:opacity-90 text-white shadow-sm transition-opacity disabled:opacity-50"
                                onClick={goNext}
                                disabled={!canNext || isWorking}
                            >
                                Next →
                            </button>
                        ) : (
                            <button
                                className="text-[13px] font-semibold px-5 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:opacity-90 text-white shadow-sm transition-opacity disabled:opacity-50"
                                onClick={onFinishImport}
                                disabled={!canNext || isWorking || validated.length === 0}
                            >
                                Finish Import
                            </button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
